#!/usr/bin/env node
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TransactionReceipt } from "@saberhq/solana-contrib";
import { Token as SToken, TokenAmount, u64 } from "@saberhq/token-utils";
import { Token, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { PublicKey } from "@solana/web3.js";
import base58 from "bs58";
import { Command } from "commander";
import { printTable } from "console-table-printer";
import { exit } from "process";
import invariant from "tiny-invariant";

import * as pack from "../package.json";
import type { MinterData, MintWrapperData } from "../src";
import { QUARRY_ADDRESSES, QUARRY_CODERS } from "../src";
import { findMinterAddress } from "../src/wrappers";
import {
  catchFinallyExit,
  confirmTx,
  getSigner,
  makeSDK,
  printObjectJSON,
  printObjectTable,
  receiptLog,
} from "./utils";

export const program = new Command().name("crema-mint-wrapper");
program.version(pack.version);

// New a mint wrapper
program
  .command("new")
  .description("Create a quarry_mint_wrapper:MintWrapper")
  .requiredOption("-m, --mint <mint>", "The token mint address")
  .argument("<hardcap>", "The token mint hardcap")
  .action((hardcap, arg) => {
    const m = new PublicKey(arg.mint);
    const h = new u64(hardcap);
    newMintWrapper(m, h)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

program
  .command("list")
  .description("Get the quarry_mint_wrapper:MintWrapper list")
  .action(() => {
    catchFinallyExit(fetchMintWrappers());
  });

program
  .command("info")
  .description("Get the quarry_mint_wrapper:MintWrapper information")
  .argument("<wrapper>", "The mint wrapper address.")
  .action((wrapper: string) => {
    const w = new PublicKey(wrapper);
    fetchWrapper(w).catch((err) => {
      console.log(err);
    });
  });


program
  .command("back-authority")
  .description("Transfer mint authority from mintWrapper to wallet")
  .requiredOption("-w, --wrapper   <mintWrapper>", "The mint wrapper address")
  .requiredOption("-a, --authority <destAuthority>", "The dest Authority ")
  .action((arg) => {
    const w = new PublicKey(arg.wrapper);
    const a = new PublicKey(arg.authority);
    catchFinallyExit(transferAuthority(w, a));
  });

program
  .command("transfer-authority")
  .description("Transfer mint authority to mintWrapper")
  .requiredOption("-w, --wrapper   <mintWrapper>", "The mint wrapper address")
  .action((arg) => {
    const w = new PublicKey(arg.wrapper);
    catchFinallyExit(transferMintAuthorityToMintWrapper(w));
  });

/// The minter sub commands
const minterCmd = program
  .command("minter")
  .description("The quarry_mint_wrapper:Minter sub commands");

minterCmd
  .command("new")
  .description("Create a new quarry_mint_wrapper:Minter")
  .requiredOption("-w, --wrapper <mintWrapper>", "The mint wrapper address")
  .requiredOption("-a, --allowance <allowance>", "The minter allowance")
  .argument("<authority>", "The minter authority address")
  .action((authority, arg) => {
    const w = new PublicKey(arg.wrapper);
    const a = new PublicKey(authority);
    const allowance = new u64(arg.allowance);
    newMinter(w, a, allowance)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

minterCmd
  .command("info")
  .description("Get the quarry_mint_wrapper:Minter information")
  .requiredOption("-w, --wrapper   <mintWrapper>", "The mint wrapper address")
  .requiredOption("-a, --authority <authority>", "The authority of the minter")
  .action((arg) => {
    const w = new PublicKey(arg.wrapper);
    const a = new PublicKey(arg.authority);
    fetchMinter(w, a)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

minterCmd
  .command("list")
  .description("Get quarry_mint_wrapper:Minter list")
  .option("-w, --wrapper <mintWrapper>", "The mint wrapper address")
  .action((arg) => {
    const wrapper =
      arg.wrapper !== undefined ? new PublicKey(arg.wrapper) : null;
    catchFinallyExit(fetchMinters(wrapper));
  });

minterCmd
  .command("update")
  .description("Update the quarry_mint_wrapper:Minter allowance")
  .requiredOption("-w, --wrapper   <mintWrapper>", "The mint wrapper address")
  .requiredOption("-a, --authority <authority>", "The authority of the minter")
  .argument("<allowance>", "The minter's new allowance.")
  .action((allowance, arg) => {
    const w = new PublicKey(arg.wrapper);
    const a = new PublicKey(arg.authority);
    const al = new u64(allowance);
    minterUpdate(w, a, al)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

minterCmd
  .command("mint")
  .description("Update the quarry_mint_wrapper:Minter allowance")
  .requiredOption("-w, --wrapper   <mintWrapper>", "The mint wrapper address")
  .requiredOption("-o, --owner <destOwner>", "The dest owner ")
  .argument("<amount>", "The minter's new allowance.")
  .action((amount, arg) => {
    const w = new PublicKey(arg.wrapper);
    const o = new PublicKey(arg.owner);
    const a = new u64(amount);
    catchFinallyExit(mintTo(w, o, a));
  });



async function newMintWrapper(
  mint: PublicKey,
  hardcap: u64
): Promise<TransactionReceipt> {
  const sdk = makeSDK();

  const payer = getSigner();

  const { tx, mintWrapper: wrapperKey } = await sdk.mintWrapper.newWrapper({
    hardcap,
    tokenMint: mint,
  });
  console.log(wrapperKey.toBase58());

  tx.instructions.unshift(
    Token.createSetAuthorityInstruction(
      TOKEN_PROGRAM_ID,
      mint,
      wrapperKey,
      "FreezeAccount",
      payer.publicKey,
      [payer]
    )
  );

  tx.instructions.unshift(
    Token.createSetAuthorityInstruction(
      TOKEN_PROGRAM_ID,
      mint,
      wrapperKey,
      "MintTokens",
      payer.publicKey,
      [payer]
    )
  );

  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function newMinter(
  wrapper: PublicKey,
  authority: PublicKey,
  allowance: u64
): Promise<TransactionReceipt> {
  const sdk = makeSDK();

  const tx = await sdk.mintWrapper.newMinter(wrapper, authority);
  const updateTx = await sdk.mintWrapper.minterUpdate(
    wrapper,
    authority,
    allowance
  );
  tx.instructions.push(...updateTx.instructions);

  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function minterUpdate(
  wrapper: PublicKey,
  authority: PublicKey,
  allowance: u64
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const tx = await sdk.mintWrapper.minterUpdate(wrapper, authority, allowance);
  const receipt = await tx.confirm();
  printLog(receipt);

  return receipt;
}

async function fetchWrapper(wrapper: PublicKey) {
  const sdk = makeSDK();
  const info = await sdk.mintWrapper.fetchMintWrapper(wrapper);
  if (info === null) {
    console.log("The mint wrapper: ", wrapper.toBase58(), "not found.");
    return;
  }
  printObjectTable({
    wrapper: wrapper,
    ...info,
  });
}

async function fetchMinter(wrapper: PublicKey, authority: PublicKey) {
  const sdk = makeSDK();
  const info = await sdk.mintWrapper.fetchMinter(wrapper, authority);
  if (info === null) {
    console.log("The minter not found");
    return;
  }
  const [minterAddress] = await findMinterAddress(
    wrapper,
    authority,
    sdk.mintWrapper.program.programId
  );
  printObjectTable({
    minter: minterAddress,
    ...info,
  });
}

async function fetchMintWrappers() {
  const sdk = makeSDK();
  const accounts = await sdk.provider.connection.getProgramAccounts(
    QUARRY_ADDRESSES.MintWrapper,
    {
      filters: [
        {
          memcmp: {
            offset: 0,
            bytes: base58.encode(
              QUARRY_CODERS.MintWrapper.accounts.mintWrapper.discriminator
            ),
          },
        },
      ],
    }
  );
  const list: any[] = [];
  accounts.forEach((accountInfo) => {
    const wrapper =
      QUARRY_CODERS.MintWrapper.coder.accounts.decode<MintWrapperData>(
        "MintWrapper",
        accountInfo.account.data
      );
    list.push({
      mintWrapper: accountInfo.pubkey.toBase58(),
      tokenMint: wrapper.tokenMint.toBase58(),
      hardcap: wrapper.hardCap,
      admin: wrapper.admin.toBase58(),
      numMinters: wrapper.numMinters,
      tokenAllowance: wrapper.totalAllowance,
      tokenMinted: wrapper.totalMinted,
    });
  });
  printTable(list);
}

async function fetchMinters(mintWrapper: PublicKey | null) {
  const sdk = makeSDK();
  const filters = [
    {
      memcmp: {
        offset: 0,
        bytes: base58.encode(
          QUARRY_CODERS.MintWrapper.accounts.minter.discriminator
        ),
      },
    },
  ];
  if (mintWrapper !== null) {
    filters.push({
      memcmp: {
        offset: 8,
        bytes: mintWrapper.toBase58(),
      },
    });
  }

  const accounts = await sdk.provider.connection.getProgramAccounts(
    QUARRY_ADDRESSES.MintWrapper,
    {
      filters,
    }
  );

  const list: any[] = [];
  accounts.forEach((accountInfo) => {
    const minter = QUARRY_CODERS.MintWrapper.coder.accounts.decode<MinterData>(
      "Minter",
      accountInfo.account.data
    );
    list.push({
      authority: minter.minterAuthority,
      mintWrapper: minter.mintWrapper.toBase58(),
      minter: accountInfo.pubkey.toBase58(),
      totalMinted: minter.totalMinted,
      totalAllowance: minter.allowance,
    });
  });
  printTable(list);
}

async function mintTo(
  wrapperKey: PublicKey,
  owner: PublicKey,
  amount: u64
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const wrapper = await sdk.mintWrapper.fetchMintWrapper(wrapperKey);
  invariant(wrapper !== null, "wrapper not found");
  const token = await SToken.load(sdk.provider.connection, wrapper.tokenMint);
  invariant(token !== null, "token not found");
  const tx = await sdk.mintWrapper.performMintTo({
    amount: new TokenAmount(token, amount),
    mintWrapper: wrapperKey,
    destOwner: owner,
  });

  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function transferAuthority(
  mintWrapper: PublicKey,
  authority: PublicKey
): Promise<TransactionReceipt | null> {
  const sdk = makeSDK();
  const tx = await sdk.mintWrapper.transferAuthority({
    mintWrapper,
    authority,
  });
  if (tx === null) {
    console.log("mint wrapper not found");
    return null;
  }
  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function transferMintAuthorityToMintWrapper(
  wrapperKey: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const wrapper = await sdk.mintWrapper.fetchMintWrapper(wrapperKey);
  invariant(wrapper !== null, "wrapper not found");
  const payer = getSigner();

  const tx = sdk.provider.newTX([
    Token.createSetAuthorityInstruction(
      TOKEN_PROGRAM_ID,
      wrapper.tokenMint,
      wrapperKey,
      "MintTokens",
      payer.publicKey,
      [payer]
    ),
  ]);

  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}
function printLog(receipt: TransactionReceipt) {
  const events: Map<string, any> = new Map();
  QUARRY_CODERS.MintWrapper.parseProgramLogEvents(
    receipt.response.meta?.logMessages ?? []
  ).forEach((event) => {
    events.set(event.name, event.data);
  });
  const e = Object.fromEntries(events);

  printObjectJSON(
    {
      ...receiptLog(receipt),
      events: e,
    },
    2
  );
}
